// src/pages/OrganizationsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, CircularProgress, Alert, Avatar, Button, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton 
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchOrganizations, createOrganization, updateOrganization, deleteOrganization } from '../api/apiService';

export default function OrganizationsPage() {
  // 状态管理
  const [organizations, setOrganizations] = useState([]); // 存储社团列表
  const [loading, setLoading] = useState(true);          // 控制加载动画
  const [error, setError] = useState(null);               // 存储错误信息

  // 对话框 (弹窗) 相关的状态
  const [dialogOpen, setDialogOpen] = useState(false);    // 控制弹窗的显示/隐藏
  const [isEditing, setIsEditing] = useState(false);      // 判断是新增还是编辑模式
  const [currentOrg, setCurrentOrg] = useState({ id: null, name: '', avatar: '' }); // 存储当前正在编辑或新增的社团信息

  // 使用 useCallback 包装数据加载函数，避免不必要的重新创建
  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrganizations();
      setOrganizations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []); // 空依赖数组表示此函数永不改变

  // 页面首次加载时，执行一次数据加载
  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  // --- 事件处理函数 ---

  // 打开“新增”弹窗
  const handleOpenCreateDialog = () => {
    setIsEditing(false);
    setCurrentOrg({ id: null, name: '', avatar: '' });
    setDialogOpen(true);
  };

  // 打开“编辑”弹窗
  const handleOpenEditDialog = (org) => {
    setIsEditing(true);
    setCurrentOrg(org);
    setDialogOpen(true);
  };

  // 关闭弹窗
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // 点击“保存”按钮
  const handleSave = async () => {
    try {
      if (isEditing) {
        // 如果是编辑模式，调用更新API
        await updateOrganization(currentOrg.id, { name: currentOrg.name, avatar: currentOrg.avatar });
      } else {
        // 否则，调用新增API
        await createOrganization({ name: currentOrg.name, avatar: currentOrg.avatar });
      }
      handleCloseDialog();      // 关闭弹窗
      loadOrganizations();      // 重新加载数据，刷新表格
    } catch (err) {
      alert(`保存失败: ${err.message}`);
    }
  };

  // 点击“删除”按钮
  const handleDelete = async (id) => {
    // 使用 window.confirm 提供一个简单的二次确认
    if (window.confirm('您确定要删除这个社团吗？此操作不可撤销。')) {
      try {
        await deleteOrganization(id);
        loadOrganizations(); // 重新加载数据，刷新表格
      } catch (err) {
        alert(`删除失败: ${err.message}`);
      }
    }
  };

  // --- 表格列定义 ---
  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'avatar',
      headerName: '头像',
      width: 100,
      renderCell: (params) => <Avatar src={params.value ? `//${params.value}` : ''} alt={params.row.name} />,
      sortable: false,
    },
    { field: 'name', headerName: '社团名称', flex: 1 },
    {
      field: 'actions',
      headerName: '操作',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleOpenEditDialog(params.row)} color="primary">
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(params.row.id)} color="error">
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">加载数据失败: {error}</Alert>;

  return (
    // 使用 Box 组件来布局页面
    <Box sx={{ height: 'calc(100vh - 128px)', width: '100%' }}>
      {/* 页面顶部：标题和新增按钮 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">社团管理</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
          新增社团
        </Button>
      </Box>

      {/* 数据表格 */}
      <DataGrid
        rows={organizations}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        disableRowSelectionOnClick
      />

      {/* 新增/编辑的弹窗 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{isEditing ? '编辑社团' : '新增社团'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="社团名称"
            fullWidth
            variant="standard"
            value={currentOrg.name}
            onChange={(e) => setCurrentOrg({ ...currentOrg, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="头像地址 (例如: i0.hdslb.com/bfs/face/xxx.jpg)"
            fullWidth
            variant="standard"
            value={currentOrg.avatar}
            onChange={(e) => setCurrentOrg({ ...currentOrg, avatar: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleSave}>保存</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}